import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';
import { IBillingAccount } from '@dch/billing-business-logic';
import { CustomerAccount } from '@dch/common-business-logic';
import {
  AnalyticsService,
  EnvironmentService,
  TranslationService,
} from '@dch/ng-common';
import isEqual from 'lodash.isequal';
import { distinctUntilChanged, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { getBillingBalanceRequest } from 'src/app/state/actions/billing-balance.actions';
import { ApplicationState } from 'src/app/state/reducers';
import {
  selectBillingBalance,
  selectHasEmptyInstallbase,
} from 'src/app/state/selectors/billing-balance.selector';
import { selectCustomerAccountState } from 'src/app/state/selectors/customer-account.selector';
import { OnDestroyHookDirective } from '../common/hooks';
import { RemoteLoggerService } from '../services/remote-logger.service';
import {
  BALANCE_CALL_ERROR,
  BILLING_SERVICES_ERROR,
  TAGGED_ERROR_KEY,
  USER_CALL_ERROR,
} from '../config/my-bill.constants';

@Component({
  selector: 'my-bill-landing-container',
  templateUrl: './my-bill-landing-container.component.html',
  standalone: false,
})
export class MyBillLandingContainerComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  readonly selectCustomerAccountState$ = this.store.select(
    selectCustomerAccountState
  );

  readonly billingBalance$ = this.store
    .select(selectBillingBalance)
    .pipe(distinctUntilChanged((a, b) => isEqual(a, b)));
  readonly hasEmptyInstallbase$ = this.store.select(selectHasEmptyInstallbase);

  isLandingLoadingSkeletonDisplayed = true;
  hasBalanceFetchingError = false;
  billingAccounts?: Array<IBillingAccount>;
  customerAccount?: CustomerAccount;
  isResUser: boolean;
  mainTitle: string;
  messageBoxType: MessageBoxTypesEnum;
  messageBoxTitleKey: string;
  messageBoxContentKey: string;
  paymentMade: boolean;
  isEvolutionScreenReady: boolean;
  messageBoxContent: string;
  isUr: boolean;

  constructor(
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private remoteLoggerService: RemoteLoggerService,
    private router: Router,
    private analytics: AnalyticsService,
    private activatedRoute: ActivatedRoute,
    private environmentService: EnvironmentService
  ) {
    super();
  }

  ngOnInit() {
    this.addSubscription(
      this.hasEmptyInstallbase$
        .pipe(
          tap((value) => {
            if (value) {
              this.messageBoxType = MessageBoxTypesEnum.Information;
              this.messageBoxTitleKey = 'bills.ServiceUnavailable.message';
              this.analytics.reportEvent(
                this.getTaggingData(
                  'info message: no product found on this account'
                )
              );
            }
          })
        )
        .subscribe()
    );

    this.addSubscription(
      combineLatest([this.selectCustomerAccountState$, this.billingBalance$])
        .pipe(
          tap(([customerAccountState, billingBalanceState]) => {
            if (
              !customerAccountState.isFetching &&
              customerAccountState.customerAccount?.getSelectedCustomer()
            ) {
              // we add !billingBalanceState.billingBalance?.hasError
              // because otherwise if the balance call fails, the combineLatest with this.billingBalance$ will emit again
              // and then we will dispatch the getBillingBalanceRequest again
              // and we will be stuck in a loop
              this.customerAccount = customerAccountState.customerAccount;
              this.isResUser =
                this.customerAccount?.getSelectedCustomer().subsegment ===
                'RES';
              if (
                !this.customerAccount.isUr() &&
                billingBalanceState.billingBalance?.result === null &&
                !billingBalanceState.billingBalance?.hasError
              ) {
                this.store.dispatch(getBillingBalanceRequest());
              } else if (this.customerAccount.isUr()) {
                this.isUr = true;
                this.messageBoxType = MessageBoxTypesEnum.Information;
                this.messageBoxTitleKey = 'bills.URView.explicit.buttonTitle';
                this.messageBoxContent =
                  this.translationService.getTranslatedValueFor(
                    'bills.URView.explicit.message'
                  );
              }
            } else if (
              !customerAccountState.isFetching &&
              customerAccountState.hasError
            ) {
              this.hasBalanceFetchingError = true;
              this.messageBoxType = MessageBoxTypesEnum.Error;
              this.messageBoxTitleKey = 'bills.redesign.errors.title';
              this.messageBoxContentKey = 'bills.redesign.errors.description';
              if (
                USER_CALL_ERROR !== sessionStorage.getItem(TAGGED_ERROR_KEY)
              ) {
                sessionStorage.setItem(TAGGED_ERROR_KEY, USER_CALL_ERROR);
                this.analytics.reportEvent(
                  this.getTaggingData(USER_CALL_ERROR, BILLING_SERVICES_ERROR)
                );
                this.remoteLoggerService.logError('user call failed');
              }
            }
          })
        )
        .subscribe()
    );

    this.addSubscription(
      this.billingBalance$
        .pipe(
          withLatestFrom(this.activatedRoute.queryParams),
          tap(([billingBalanceState, params]) => {
            //Note: billingAccountNumberUrlParam not present
            //when you come from pxplus webview , as fallback reading it from ogone feedback parameters
            if (params.STATUS !== undefined && params.billingAccountNumber) {
              this.router.navigate(
                ['overview/fls/' + params.billingAccountNumber],
                {
                  queryParams: params,
                }
              );
            } else {
              this.isLandingLoadingSkeletonDisplayed =
                billingBalanceState.billingBalance.isFetching;
              if (
                !this.isLandingLoadingSkeletonDisplayed &&
                billingBalanceState.viewableBillingAccounts.length > 1
              ) {
                this.billingAccounts =
                  billingBalanceState.viewableBillingAccounts;
                this.setLandingMainTitle();
              } else if (
                billingBalanceState.viewableBillingAccounts.length === 1
              ) {
                this.router.navigate([
                  `overview/fls/${billingBalanceState.viewableBillingAccounts[0].accountNumber}`,
                ]);
              } else if (billingBalanceState.billingBalance.hasError) {
                this.hasBalanceFetchingError = true;
                this.messageBoxType = MessageBoxTypesEnum.Error;
                this.messageBoxTitleKey = 'bills.redesign.errors.title';
                this.messageBoxContentKey = 'bills.redesign.errors.description';
                if (
                  BALANCE_CALL_ERROR !==
                  sessionStorage.getItem(TAGGED_ERROR_KEY)
                ) {
                  sessionStorage.setItem(TAGGED_ERROR_KEY, BALANCE_CALL_ERROR);
                  this.analytics.reportEvent(
                    this.getTaggingData(
                      BALANCE_CALL_ERROR,
                      BILLING_SERVICES_ERROR
                    )
                  );
                }
              }
            }
          })
        )
        .subscribe()
    );

    this.isEvolutionScreenReady =
      'true' ===
      this.translationService.getTranslatedValueFor(
        'bills.evolutionChart.web.isEvolutionScreenReady'
      );
  }

  onAccountCardClick(billingAccount: IBillingAccount) {
    this.router.navigate([`overview/fls/${billingAccount.accountNumber}`]);
  }

  private setLandingMainTitle() {
    const userType = this.isResUser ? 'RES' : 'SE';
    this.mainTitle = this.translationService.getTranslatedValueFor(
      `bills.redesign.landing.titles.${userType}`
    );
  }

  private getTaggingData(name: string, errorMessage?: string) {
    return {
      event: 'popup-impression',
      popup: {
        zone: 'mybill.overview',
        name,
      },
      ...(errorMessage && {
        error: {
          type: 'front-end',
          message: errorMessage,
        },
      }),
    };
  }
}
